import type {
    ChartStaggerProgressConstantType,
    ChartTooltipPositionConstantType,
    ChartKeyboardIndexConstantType,
    ChartLabelIndexesConstantType,
    ChartLinearScaleConstantType,
    ChartSeriesColorConstantType,
    ChartTableModelConstantType,
    ChartCurvePathConstantType,
    ChartBandIndexConstantType,
    ChartNiceScaleConstantType,
    ChartTextWidthConstantType,
    ChartAreaPathConstantType,
    ChartPlotRectConstantType,
    ChartArcPathConstantType,
    ChartBarPathConstantType,
    ChartPatternConstantType,
    ChartPointXConstantType,
    ChartXTicksConstantType,
    ChartBandConstantType,
    ChartSkeletonKind,
    ChartSeriesColor,
    ChartTableModel,
    ChartDataValue,
    ChartNiceScale,
    ChartPattern,
    ChartPoint,
    ChartCurve,
    ChartRect,
    ChartBand,
    ChartKind
} from "./type";
import {
    MOTION_EASINGS,
    MOTION_INTENTS
} from "../../helpers/motion";
import {
    getHorizontalStep
} from "../../helpers/direction";
import {
    formatNumber
} from "../../helpers/locale";

type ChartCurvePiece = {
    control1?: ChartPoint;
    control2?: ChartPoint;
    kind: ChartCurve;
    from: ChartPoint;
    to: ChartPoint;
};

export const CHART_PATTERNS: Array<ChartPattern> = [
    "none",
    "hatch45",
    "hatch135",
    "hatchx"
];

export const CHART_PATTERN_ANGLES: Record<ChartPattern, Array<number>> = {
    hatch135: [135],
    hatch45: [45],
    hatchx: [
        45,
        135
    ],
    none: []
};

export const CHART_TICK_COUNT = 4;

export const CHART_TABLE_GAP = "–";

export const CHART_NICE_STEPS = [
    1,
    2,
    2.5,
    5,
    10
];

export const CHART_TEXT_WIDTH_RATIO = 0.66;

export const CHART_PATH_DECIMALS = 100;

export const CHART_STAGGER_LIMIT = 8;

export const CHART_FULL_CIRCLE = Math.PI * 2;

export const CHART_ANGLE_EPSILON = 1e-6;

export const CHART_COMPACT_BASE = 1000;

export const CHART_COMPACT_FRACTION_DIGITS = 1;

export const CHART_TICK_STEP_SLACK = 1.5;

export const CHART_COMPACT_UNITS: Record<string, Array<string>> = {
    tr: [
        "B",
        "Mn",
        "Mr",
        "Tn"
    ],
    en: [
        "K",
        "M",
        "B",
        "T"
    ]
};

export const CHART_COMPACT_SEPARATORS: Record<string, string> = {
    tr: "\u00A0",
    en: ""
};

export const CHART_SKELETON_KINDS: Record<ChartKind, ChartSkeletonKind> = {
    radial: "radial",
    radar: "radar",
    area: "line",
    line: "line",
    bar: "bars",
    pie: "pie"
};

const CHART_COMPACT_SUPPORT = new Map<string, boolean>();

export const getChartSeriesColor = ({
    colorIndex,
    colors
}: ChartSeriesColorConstantType): ChartSeriesColor => {
    if(colorIndex === "muted") {
        return {
            areaColor: colors.chart.mutedArea,
            color: colors.chart.muted
        };
    }

    return {
        areaColor: colors.chart.categoricalArea[colorIndex],
        color: colors.chart.categorical[colorIndex]
    };
};

export const getChartPattern = ({
    isPatterned,
    colorIndex
}: ChartPatternConstantType): ChartPattern => {
    if(!isPatterned || colorIndex === "muted") {
        return "none";
    }

    return CHART_PATTERNS[(colorIndex - 1) % CHART_PATTERNS.length] ?? "none";
};

export const getChartNiceScale = ({
    count,
    min,
    max
}: ChartNiceScaleConstantType): ChartNiceScale => {
    const low = Math.min(0, min);

    const high = Math.max(0, max);

    const span = high - low || 1;

    const rawStep = span / count;

    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));

    const normalized = rawStep / magnitude;

    const step = (CHART_NICE_STEPS.find((item) => normalized <= item) ?? 10) * magnitude;

    const niceMin = Math.floor(low / step) * step;

    const niceMax = Math.ceil(high / step) * step;

    const ticks: Array<number> = [];

    for(let tick = niceMin; tick <= niceMax + step / 2; tick += step) {
        ticks.push(Math.round(tick / step) * step);
    }

    return {
        max: niceMax,
        min: niceMin,
        ticks
    };
};

export const getChartLinearScale = ({
    domain,
    range
}: ChartLinearScaleConstantType) => {
    const span = domain[1] - domain[0] || 1;

    return (value: number): number => {
        return range[0] + (value - domain[0]) / span * (range[1] - range[0]);
    };
};

export const getChartPlotRect = ({
    xAxisHeight,
    yAxisWidth,
    height,
    inset,
    isRTL,
    width
}: ChartPlotRectConstantType): ChartRect => {
    return {
        height: Math.max(0, height - xAxisHeight - inset),
        width: Math.max(0, width - yAxisWidth),
        x: isRTL ? 0 : yAxisWidth,
        y: inset
    };
};

export const getChartTextWidth = ({
    fontSize,
    text
}: ChartTextWidthConstantType): number => {
    return text.length * fontSize * CHART_TEXT_WIDTH_RATIO;
};

export const getChartBand = ({
    isRTL = false,
    count,
    index,
    start,
    size
}: ChartBandConstantType): ChartBand => {
    const bandSize = size / Math.max(1, count);

    const offset = (isRTL ? count - 1 - index : index) * bandSize;

    return {
        center: start + offset + bandSize / 2,
        start: start + offset,
        size: bandSize
    };
};

export const getChartBandIndex = ({
    isRTL = false,
    position,
    count,
    start,
    size
}: ChartBandIndexConstantType): number | undefined => {
    if(!count || position < start || position > start + size) {
        return undefined;
    }

    const index = Math.min(count - 1, Math.floor((position - start) / (size / count)));

    return isRTL ? count - 1 - index : index;
};

export const getChartKeyboardIndex = ({
    direction,
    count,
    index,
    key
}: ChartKeyboardIndexConstantType): number | undefined => {
    if(!count) {
        return undefined;
    }

    if(key === "Home") {
        return 0;
    }

    if(key === "End") {
        return count - 1;
    }

    const step = getHorizontalStep({
        direction,
        key
    });

    if(!step) {
        return undefined;
    }

    if(index === undefined) {
        return step > 0 ? 0 : count - 1;
    }

    return Math.min(count - 1, Math.max(0, index + step));
};

export const getChartLabelIndexes = ({
    behaviour,
    values
}: ChartLabelIndexesConstantType): Array<number> => {
    if(!values.length || behaviour === "none") {
        return [];
    }

    if(behaviour === "all") {
        return values.map((_, index) => index);
    }

    if(behaviour === "end") {
        return [
            values.length - 1
        ];
    }

    const maxValue = Math.max(...values);

    const minValue = Math.min(...values);

    return [
        values.indexOf(maxValue),
        values.indexOf(minValue)
    ].filter((item, index, list) => list.indexOf(item) === index);
};

export const getChartTooltipPosition = ({
    behaviour = "follow",
    container,
    tooltip,
    anchor,
    margin,
    isRTL,
    gap
}: ChartTooltipPositionConstantType) => {
    const maxLeft = Math.max(0, container.width - tooltip.width);

    const maxTop = Math.max(0, container.height - tooltip.height);

    const clampLeft = (value: number) => Math.max(0, Math.min(value, maxLeft));

    const clampTop = (value: number) => Math.max(0, Math.min(value, maxTop));

    if(behaviour === "pinned") {
        return {
            left: clampLeft(anchor.x - tooltip.width / 2),
            top: 0
        };
    }

    const isFitting = (start: number) => start >= margin && start + tooltip.width <= container.width - margin;

    const markStart = Math.min(anchor.x, anchor.xStart ?? anchor.x);

    const markEnd = Math.max(anchor.x, anchor.xStart ?? anchor.x);

    const preferredStart = isRTL ? markStart - gap - tooltip.width : markEnd + gap;

    const fallbackStart = isRTL ? markEnd + gap : markStart - gap - tooltip.width;

    if(isFitting(preferredStart) || isFitting(fallbackStart)) {
        return {
            left: isFitting(preferredStart) ? preferredStart : fallbackStart,
            top: clampTop(anchor.y - tooltip.height / 2)
        };
    }

    const above = anchor.y - gap - tooltip.height;

    return {
        top: above >= 0 ? above : clampTop(anchor.y + gap),
        left: clampLeft(anchor.x - tooltip.width / 2)
    };
};

const round = (value: number): number => {
    return Math.round(value * CHART_PATH_DECIMALS) / CHART_PATH_DECIMALS;
};

export const getChartBarPath = ({
    height,
    radius,
    width,
    side,
    x,
    y
}: ChartBarPathConstantType): string => {
    const maxRadius = side === "start" || side === "end" ? Math.min(radius, height / 2, width) : Math.min(radius, width / 2, height);

    const r = Math.max(0, maxRadius);

    const left = round(x);

    const top = round(y);

    const right = round(x + width);

    const bottom = round(y + height);

    const rr = round(r);

    if(!r || side === "none") {
        return `M${left},${top}H${right}V${bottom}H${left}Z`;
    }

    if(side === "top") {
        return `M${left},${bottom}V${round(top + r)}A${rr},${rr} 0 0 1 ${round(left + r)},${top}H${round(right - r)}A${rr},${rr} 0 0 1 ${right},${round(top + r)}V${bottom}Z`;
    }

    if(side === "bottom") {
        return `M${left},${top}H${right}V${round(bottom - r)}A${rr},${rr} 0 0 1 ${round(right - r)},${bottom}H${round(left + r)}A${rr},${rr} 0 0 1 ${left},${round(bottom - r)}Z`;
    }

    if(side === "end") {
        return `M${left},${top}H${round(right - r)}A${rr},${rr} 0 0 1 ${right},${round(top + r)}V${round(bottom - r)}A${rr},${rr} 0 0 1 ${round(right - r)},${bottom}H${left}Z`;
    }

    return `M${right},${top}H${round(left + r)}A${rr},${rr} 0 0 0 ${left},${round(top + r)}V${round(bottom - r)}A${rr},${rr} 0 0 0 ${round(left + r)},${bottom}H${right}Z`;
};

export const getChartTableModel = ({
    totalTitle,
    config,
    model
}: ChartTableModelConstantType): ChartTableModel => {
    const columns = [
        model.categoryTitle ?? "",
        ...model.seriesKeys.map((key) => config[key]?.title ?? key)
    ];

    const isShowTotal = model.isShowTotal ?? model.isStacked ?? false;

    if(isShowTotal) {
        columns.push(totalTitle);
    }

    const rows = model.data.map((row, index) => {
        const categoryValue = model.categoryKey ? row[model.categoryKey] : index + 1;

        const values = model.seriesKeys.map((key) => {
            const value: ChartDataValue | null | undefined = row[key];

            return value === undefined || value === null || value === "" ? undefined : Number(value);
        });

        const cells = [
            model.categoryFormatter ? model.categoryFormatter(categoryValue ?? index, index) : String(categoryValue ?? index),
            ...values.map((value) => value === undefined ? CHART_TABLE_GAP : model.valueFormatter(value))
        ];

        if(isShowTotal) {
            cells.push(model.valueFormatter(values.reduce<number>((total, value) => total + (value ?? 0), 0)));
        }

        return cells;
    });

    return {
        columns,
        rows
    };
};

const formatChartPoint = (point: ChartPoint): string => {
    return `${round(point.x)},${round(point.y)}`;
};

const getChartPointSegments = (points: Array<ChartPoint | null | undefined>): Array<Array<ChartPoint>> => {
    const segments: Array<Array<ChartPoint>> = [];

    let current: Array<ChartPoint> = [];

    points.forEach((point) => {
        if(!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) {
            if(current.length) {
                segments.push(current);
            }

            current = [];

            return;
        }

        current.push(point);
    });

    if(current.length) {
        segments.push(current);
    }

    return segments;
};

const getChartMonotoneTangents = (points: Array<ChartPoint>): Array<number> => {
    const count = points.length;

    const widths = points.slice(0, -1).map((point, index) => (points[index + 1]?.x ?? point.x) - point.x);

    const slopes = points.slice(0, -1).map((point, index) => {
        const width = widths[index] ?? 0;

        return width ? ((points[index + 1]?.y ?? point.y) - point.y) / width : 0;
    });

    const tangents = points.map(() => 0);

    for(let index = 1; index < count - 1; index += 1) {
        const previousSlope = slopes[index - 1] ?? 0;

        const nextSlope = slopes[index] ?? 0;

        const previousWidth = widths[index - 1] ?? 0;

        const nextWidth = widths[index] ?? 0;

        const span = previousWidth + nextWidth;

        const weighted = span ? (previousSlope * nextWidth + nextSlope * previousWidth) / span : 0;

        tangents[index] = (Math.sign(previousSlope) + Math.sign(nextSlope)) * Math.min(Math.abs(previousSlope), Math.abs(nextSlope), Math.abs(weighted) / 2) || 0;
    }

    tangents[0] = ((slopes[0] ?? 0) * 3 - (tangents[1] ?? 0)) / 2;

    tangents[count - 1] = ((slopes[count - 2] ?? 0) * 3 - (tangents[count - 2] ?? 0)) / 2;

    return tangents;
};

const getChartCurvePieces = (points: Array<ChartPoint>, curve: ChartCurve): Array<ChartCurvePiece> => {
    const pairs = points.slice(0, -1).map((from, index) => ({
        to: points[index + 1] ?? from,
        from
    }));

    if(curve !== "natural" || points.length < 3) {
        return pairs.map((pair) => ({
            ...pair,
            kind: curve === "step" ? "step" : "linear"
        }));
    }

    const tangents = getChartMonotoneTangents(points);

    return pairs.map((pair, index) => {
        const third = (pair.to.x - pair.from.x) / 3;

        return {
            control2: {
                y: pair.to.y - third * (tangents[index + 1] ?? 0),
                x: pair.to.x - third
            },
            control1: {
                y: pair.from.y + third * (tangents[index] ?? 0),
                x: pair.from.x + third
            },
            kind: "natural",
            ...pair
        };
    });
};

const getChartPieceCommand = (piece: ChartCurvePiece, isReversed: boolean): string => {
    const target = isReversed ? piece.from : piece.to;

    if(piece.kind === "step") {
        return isReversed ? `V${round(piece.from.y)}H${round(piece.from.x)}` : `H${round(piece.to.x)}V${round(piece.to.y)}`;
    }

    if(piece.kind === "natural" && piece.control1 && piece.control2) {
        const first = isReversed ? piece.control2 : piece.control1;

        const second = isReversed ? piece.control1 : piece.control2;

        return `C${formatChartPoint(first)} ${formatChartPoint(second)} ${formatChartPoint(target)}`;
    }

    return `L${formatChartPoint(target)}`;
};

const getChartClosedNaturalPath = (points: Array<ChartPoint>): string => {
    const count = points.length;

    const getPoint = (index: number): ChartPoint => points[(index + count) % count] ?? {
        x: 0,
        y: 0
    };

    const commands = points.map((current, index) => {
        const previous = getPoint(index - 1);

        const next = getPoint(index + 1);

        const after = getPoint(index + 2);

        return `C${formatChartPoint({
            x: current.x + (next.x - previous.x) / 6,
            y: current.y + (next.y - previous.y) / 6
        })} ${formatChartPoint({
            x: next.x - (after.x - current.x) / 6,
            y: next.y - (after.y - current.y) / 6
        })} ${formatChartPoint(next)}`;
    });

    return `M${formatChartPoint(getPoint(0))}${commands.join("")}Z`;
};

export const getChartCurvePath = ({
    isClosed = false,
    points,
    curve
}: ChartCurvePathConstantType): string => {
    const segments = getChartPointSegments(points);

    const firstSegment = segments[0];

    if(isClosed && segments.length === 1 && firstSegment && firstSegment.length > 2) {
        if(curve === "natural") {
            return getChartClosedNaturalPath(firstSegment);
        }

        const pieces = getChartCurvePieces([
            ...firstSegment,
            firstSegment[0] ?? {
                x: 0,
                y: 0
            }
        ], curve);

        return `M${formatChartPoint(firstSegment[0] ?? {
            x: 0,
            y: 0
        })}${pieces.map((piece) => getChartPieceCommand(piece, false)).join("")}Z`;
    }

    return segments.map((segment) => {
        const start = segment[0];

        if(!start) {
            return "";
        }

        if(segment.length === 1) {
            return `M${formatChartPoint(start)}L${formatChartPoint(start)}`;
        }

        return `M${formatChartPoint(start)}${getChartCurvePieces(segment, curve).map((piece) => getChartPieceCommand(piece, false)).join("")}`;
    }).join("");
};

export const getChartAreaPath = ({
    bottom,
    curve,
    top
}: ChartAreaPathConstantType): string => {
    const isBaseline = typeof bottom === "number";

    const getBottom = (index: number): ChartPoint | null | undefined => {
        const point = top[index];

        if(typeof bottom === "number") {
            return point ? {
                x: point.x,
                y: bottom
            } : undefined;
        }

        return bottom[index];
    };

    const pairs = top.map((point, index) => {
        const base = getBottom(index);

        return point && base && Number.isFinite(base.y) ? {
            bottom: base,
            top: point
        } : undefined;
    });

    const segments: Array<Array<{ bottom: ChartPoint; top: ChartPoint }>> = [];

    let current: Array<{ bottom: ChartPoint; top: ChartPoint }> = [];

    pairs.forEach((pair) => {
        if(!pair) {
            if(current.length) {
                segments.push(current);
            }

            current = [];

            return;
        }

        current.push(pair);
    });

    if(current.length) {
        segments.push(current);
    }

    return segments.filter((segment) => segment.length > 1).map((segment) => {
        const topPoints = segment.map((pair) => pair.top);

        const bottomPoints = segment.map((pair) => pair.bottom);

        const first = topPoints[0];

        const lastBottom = bottomPoints[bottomPoints.length - 1];

        if(!first || !lastBottom) {
            return "";
        }

        const topCommands = getChartCurvePieces(topPoints, curve).map((piece) => getChartPieceCommand(piece, false)).join("");

        const bottomCommands = isBaseline ? `L${formatChartPoint(bottomPoints[0] ?? lastBottom)}` : getChartCurvePieces(bottomPoints, curve).reverse().map((piece) => getChartPieceCommand(piece, true)).join("");

        return `M${formatChartPoint(first)}${topCommands}L${formatChartPoint(lastBottom)}${bottomCommands}Z`;
    }).join("");
};

export const getChartPointX = ({
    isRTL = false,
    count,
    index,
    start,
    size
}: ChartPointXConstantType): number => {
    if(count <= 1) {
        return start + size / 2;
    }

    const offset = size * index / (count - 1);

    return isRTL ? start + size - offset : start + offset;
};

export const getChartXTicks = ({
    scale = "point",
    labelWidth,
    minGap,
    count,
    width
}: ChartXTicksConstantType): Array<number> => {
    if(count <= 0) {
        return [];
    }

    const last = count - 1;

    if(!last) {
        return [
            0
        ];
    }

    const unit = width / (scale === "band" ? count : last);

    const minStep = unit > 0 ? Math.max(1, Math.ceil((labelWidth + minGap) / unit)) : count;

    if(minStep > last) {
        return [
            0
        ];
    }

    const maxStep = Math.min(last, Math.floor(minStep * CHART_TICK_STEP_SLACK));

    let step = minStep;

    for(let candidate = minStep; candidate <= maxStep; candidate += 1) {
        if(last % candidate === 0) {
            step = candidate;

            break;
        }
    }

    const indexes: Array<number> = [];

    for(let index = 0; index <= last; index += step) {
        indexes.push(index);
    }

    return indexes;
};

export const getChartStaggerProgress = ({
    intent = "chartEnter",
    duration,
    progress,
    stagger,
    index,
    count
}: ChartStaggerProgressConstantType): number => {
    if(progress >= 1 || duration <= 0) {
        return 1;
    }

    const step = count > 1 ? stagger * Math.min(1, CHART_STAGGER_LIMIT / (count - 1)) : 0;

    const elapsed = Math.max(0, progress) * (duration + CHART_STAGGER_LIMIT * stagger);

    const local = Math.max(0, Math.min(1, (elapsed - index * step) / duration));

    return MOTION_EASINGS[MOTION_INTENTS[intent].easing](local);
};

export const getChartArcPath = ({
    cornerRadius = 0,
    isRTL = false,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    cx,
    cy
}: ChartArcPathConstantType): string => {
    const start = Math.min(startAngle, endAngle);

    const sweep = Math.min(CHART_FULL_CIRCLE, Math.abs(endAngle - startAngle));

    const outer = Math.max(0, outerRadius);

    const inner = Math.max(0, Math.min(innerRadius, outer));

    if(sweep < CHART_ANGLE_EPSILON || outer <= 0) {
        return "";
    }

    const sign = isRTL ? -1 : 1;

    const forwardFlag = isRTL ? 0 : 1;

    const backwardFlag = isRTL ? 1 : 0;

    const getPoint = (radius: number, angle: number): string => formatChartPoint({
        x: cx + sign * radius * Math.sin(angle),
        y: cy - radius * Math.cos(angle)
    });

    const getArc = (radius: number, angle: number, size: number, flag: number): string => {
        const rr = round(radius);

        return `A${rr},${rr} 0 ${size > Math.PI ? 1 : 0} ${flag} ${getPoint(radius, angle)}`;
    };

    const isFull = sweep >= CHART_FULL_CIRCLE - CHART_ANGLE_EPSILON;

    const end = start + sweep;

    const middle = start + sweep / 2;

    if(inner === outer) {
        if(isFull) {
            return `M${getPoint(outer, start)}${getArc(outer, middle, Math.PI, forwardFlag)}${getArc(outer, start, Math.PI, forwardFlag)}`;
        }

        return `M${getPoint(outer, start)}${getArc(outer, end, sweep, forwardFlag)}`;
    }

    if(isFull) {
        const outerRing = `M${getPoint(outer, start)}${getArc(outer, middle, Math.PI, forwardFlag)}${getArc(outer, start, Math.PI, forwardFlag)}Z`;

        if(inner <= 0) {
            return outerRing;
        }

        return `${outerRing}M${getPoint(inner, start)}${getArc(inner, middle, Math.PI, backwardFlag)}${getArc(inner, start, Math.PI, backwardFlag)}Z`;
    }

    const halfSine = sweep < Math.PI ? Math.sin(sweep / 2) : 1;

    const thickness = outer - inner;

    const outerCorner = Math.max(0, Math.min(cornerRadius, thickness / 2, outer * halfSine / (1 + halfSine)));

    const innerCorner = inner > 0 ? Math.max(0, Math.min(cornerRadius, thickness / 2, halfSine < 1 ? inner * halfSine / (1 - halfSine) : cornerRadius)) : 0;

    const center = formatChartPoint({
        x: cx,
        y: cy
    });

    if(!outerCorner && !innerCorner) {
        const outerPath = `M${getPoint(outer, start)}${getArc(outer, end, sweep, forwardFlag)}`;

        if(inner <= 0) {
            return `${outerPath}L${center}Z`;
        }

        return `${outerPath}L${getPoint(inner, end)}${getArc(inner, start, sweep, backwardFlag)}Z`;
    }

    const outerOffset = outerCorner ? Math.asin(outerCorner / (outer - outerCorner)) : 0;

    const outerEdge = outerCorner ? Math.sqrt(Math.pow(outer - outerCorner, 2) - Math.pow(outerCorner, 2)) : outer;

    const innerOffset = innerCorner ? Math.asin(innerCorner / (inner + innerCorner)) : 0;

    const innerEdge = innerCorner ? Math.sqrt(Math.pow(inner + innerCorner, 2) - Math.pow(innerCorner, 2)) : inner;

    const getCorner = (radius: number, target: string): string => {
        if(!radius) {
            return "";
        }

        const rr = round(radius);

        return `A${rr},${rr} 0 0 ${forwardFlag} ${target}`;
    };

    const outerPath = [
        `M${getPoint(outerEdge, start)}`,
        getCorner(outerCorner, getPoint(outer, start + outerOffset)),
        getArc(outer, end - outerOffset, sweep - outerOffset * 2, forwardFlag),
        getCorner(outerCorner, getPoint(outerEdge, end))
    ].join("");

    if(inner <= 0) {
        return `${outerPath}L${center}Z`;
    }

    return [
        outerPath,
        `L${getPoint(innerEdge, end)}`,
        getCorner(innerCorner, getPoint(inner, end - innerOffset)),
        getArc(inner, start + innerOffset, sweep - innerOffset * 2, backwardFlag),
        getCorner(innerCorner, getPoint(innerEdge, start)),
        "Z"
    ].join("");
};

const isChartCompactSupported = (locale: string): boolean => {
    const cached = CHART_COMPACT_SUPPORT.get(locale);

    if(cached !== undefined) {
        return cached;
    }

    let isSupported = false;

    try {
        isSupported = formatNumber({
            value: CHART_COMPACT_BASE,
            options: {
                notation: "compact"
            },
            locale
        }) !== formatNumber({
            value: CHART_COMPACT_BASE,
            locale
        });
    } catch {
        isSupported = false;
    }

    CHART_COMPACT_SUPPORT.set(locale, isSupported);

    return isSupported;
};

export const formatChartCompact = (value: number, locale: string): string => {
    if(isChartCompactSupported(locale)) {
        return formatNumber({
            options: {
                maximumFractionDigits: CHART_COMPACT_FRACTION_DIGITS,
                notation: "compact"
            },
            locale,
            value
        });
    }

    const language = locale.toLowerCase().split("-")[0] ?? "";

    const units = CHART_COMPACT_UNITS[language] ?? CHART_COMPACT_UNITS.en ?? [];

    const separator = CHART_COMPACT_SEPARATORS[language] ?? "";

    const magnitude = Math.abs(value) < CHART_COMPACT_BASE ? 0 : Math.min(units.length, Math.floor(Math.log(Math.abs(value)) / Math.log(CHART_COMPACT_BASE)));

    const text = formatNumber({
        options: {
            maximumFractionDigits: CHART_COMPACT_FRACTION_DIGITS
        },
        value: value / Math.pow(CHART_COMPACT_BASE, magnitude),
        locale
    });

    return magnitude ? `${text}${separator}${units[magnitude - 1] ?? ""}` : text;
};
